/**
 * Dialog para preview e envio de múltiplas imagens
 * 
 * Exibe grid de preview das imagens selecionadas, campo de legenda global,
 * validação de quantidade e tamanho, permite remover imagens individualmente.
 * 
 * Layouts:
 * - 1 imagem: imagem grande
 * - 2 imagens: grid 1x2 (lado a lado)
 * - 3 imagens: 1 grande + 2 pequenas (L-layout)
 * - 4+ imagens: grid 2x2 com indicador "+X imagens" no 4º slot
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Send, X, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmojiPicker } from './EmojiPicker';

interface SendMultipleImagesDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para fechar o dialog */
  onClose: () => void;
  /** Array de arquivos de imagem selecionados */
  imageFiles: File[];
  /** Callback para enviar imagens */
  onSend: (files: File[], caption?: string) => Promise<void>;
  /** Callback para adicionar mais imagens */
  onAddMore?: () => void;
}

// Limites do WhatsApp
const MAX_IMAGES = 30;
const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB por imagem

export function SendMultipleImagesDialog({
  open,
  onClose,
  imageFiles,
  onSend,
  onAddMore,
}: SendMultipleImagesDialogProps) {
  const [caption, setCaption] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  // Sincroniza localFiles com imageFiles quando dialog abre
  useEffect(() => {
    if (open && imageFiles.length > 0) {
      setLocalFiles(imageFiles);
    }
  }, [open, imageFiles]);

  // Gera previews quando arquivos mudam
  useEffect(() => {
    // Limpa previews anteriores
    previewUrls.forEach(url => URL.revokeObjectURL(url));

    // Valida arquivos e gera novos previews
    const newErrors: string[] = [];
    const validFiles: File[] = [];
    const urls: string[] = [];

    localFiles.forEach((file, index) => {
      if (file.size > MAX_FILE_SIZE) {
        newErrors.push(`Imagem ${index + 1}: arquivo muito grande (máx. 16MB)`);
      } else {
        validFiles.push(file);
        urls.push(URL.createObjectURL(file));
      }
    });

    if (localFiles.length > MAX_IMAGES) {
      newErrors.push(`Limite de ${MAX_IMAGES} imagens excedido`);
    }

    setErrors(newErrors);
    setPreviewUrls(urls);

    // Cleanup ao desmontar
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [localFiles]);

  // Limpa estado quando dialog fecha
  useEffect(() => {
    if (!open) {
      setCaption('');
      setErrors([]);
      setIsSending(false);
      setLocalFiles([]);
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      setPreviewUrls([]);
    }
  }, [open]);

  // Remove uma imagem do array
  const handleRemoveImage = (index: number) => {
    setLocalFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handler de envio (fire-and-forget):
  // Fecha o modal imediatamente e delega o envio + tratamento de erro
  // para a tela de Chat (otimismo + toast + status='failed' no balão).
  const handleSend = () => {
    if (localFiles.length === 0 || errors.length > 0) return;

    // Snapshot dos parâmetros antes de fechar (evita perda de estado no reset).
    const filesToSend = localFiles;
    const captionToSend = caption.trim() || undefined;

    // 1) Fecha o modal na hora — UX igual a WhatsApp/Telegram
    onClose();

    // 2) Dispara envio em background — erros já são tratados em Chat.tsx
    Promise.resolve(onSend(filesToSend, captionToSend)).catch((err) => {
      console.error('[SendMultipleImagesDialog] background send error:', err);
    });
  };

  // Handler para Enter no campo de caption
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSending && errors.length === 0) {
      e.preventDefault();
      handleSend();
    }
  };

  // Formata tamanho total dos arquivos
  const totalSize = useMemo(() => {
    const bytes = localFiles.reduce((sum, file) => sum + file.size, 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }, [localFiles]);

  // Quantidade de imagens extras além das 4 mostradas no grid
  const extraCount = localFiles.length > 4 ? localFiles.length - 3 : 0;

  // Renderiza o grid de preview baseado na quantidade de imagens
  const renderPreviewGrid = () => {
    if (previewUrls.length === 0) {
      return (
        <div className="h-[200px] rounded-lg bg-muted flex items-center justify-center">
          <span className="text-muted-foreground">Nenhuma imagem selecionada</span>
        </div>
      );
    }

    // 1 imagem: layout único grande
    if (previewUrls.length === 1) {
      return (
        <div className="relative rounded-lg overflow-hidden bg-muted">
          <img
            src={previewUrls[0]}
            alt="Preview 1"
            className="w-full h-auto max-h-[300px] object-contain"
          />
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6"
            onClick={() => handleRemoveImage(0)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      );
    }

    // 2 imagens: grid 1x2 (lado a lado)
    if (previewUrls.length === 2) {
      return (
        <div className="grid grid-cols-2 gap-2">
          {previewUrls.map((url, index) => (
            <div key={index} className="relative rounded-lg overflow-hidden bg-muted aspect-square">
              <img
                src={url}
                alt={`Preview ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-6 w-6"
                onClick={() => handleRemoveImage(index)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      );
    }

    // 3 imagens: 1 grande + 2 pequenas (L-layout)
    if (previewUrls.length === 3) {
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="relative rounded-lg overflow-hidden bg-muted row-span-2 aspect-square">
            <img
              src={previewUrls[0]}
              alt="Preview 1"
              className="w-full h-full object-cover"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6"
              onClick={() => handleRemoveImage(0)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
          {previewUrls.slice(1, 3).map((url, index) => (
            <div key={index + 1} className="relative rounded-lg overflow-hidden bg-muted aspect-square">
              <img
                src={url}
                alt={`Preview ${index + 2}`}
                className="w-full h-full object-cover"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-6 w-6"
                onClick={() => handleRemoveImage(index + 1)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      );
    }

    // 4+ imagens: grid 2x2 com indicador +X no último slot
    return (
      <div className="grid grid-cols-2 gap-2">
        {previewUrls.slice(0, 3).map((url, index) => (
          <div key={index} className="relative rounded-lg overflow-hidden bg-muted aspect-square">
            <img
              src={url}
              alt={`Preview ${index + 1}`}
              className="w-full h-full object-cover"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6"
              onClick={() => handleRemoveImage(index)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
        {/* Último slot com contador +X */}
        <div className="relative rounded-lg overflow-hidden bg-muted aspect-square">
          <img
            src={previewUrls[3]}
            alt="Preview 4"
            className="w-full h-full object-cover"
          />
          {extraCount > 0 && (
            <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
              <span className="text-2xl font-bold text-foreground">
                +{extraCount}
              </span>
            </div>
          )}
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6 z-10"
            onClick={() => handleRemoveImage(3)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSending && onClose()}>
      <DialogContent 
        className="max-w-md"
        onInteractOutside={(e) => isSending && e.preventDefault()}
        onPointerDownOutside={(e) => isSending && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            Enviar {localFiles.length} {localFiles.length === 1 ? 'Imagem' : 'Imagens'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview grid das imagens */}
          {renderPreviewGrid()}

          {/* Info dos arquivos */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {localFiles.length} de {MAX_IMAGES} imagens
            </span>
            <span>Tamanho total: {totalSize}</span>
          </div>

          {/* Erros */}
          {errors.length > 0 && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm space-y-1">
              {errors.map((error, index) => (
                <div key={index} className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              ))}
            </div>
          )}

          {/* Campo de legenda — com EmojiPicker reutilizado do input principal do chat */}
          <div className="space-y-2">
            <Label htmlFor="caption">Legenda (opcional)</Label>
            {/* Wrapper relative para posicionar o botão de emoji sobre o textarea */}
            <div className="relative">
              {/* Textarea: Enter envia, Shift+Enter quebra linha (igual ao chat) */}
              <Textarea
                id="caption"
                placeholder="Adicione uma legenda para todas as imagens... (Shift+Enter para nova linha)"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSending}
                maxLength={1024}
                rows={2}
                className="resize-none min-h-[40px] max-h-[120px] pr-10"
              />
              {/* Botão de emoji: append simples ao final da legenda */}
              <EmojiPicker
                onSelect={(emoji) => setCaption((prev) => prev + emoji)}
                disabled={isSending}
                className="absolute bottom-2 right-2"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {onAddMore && localFiles.length < MAX_IMAGES && (
            <Button
              variant="outline"
              onClick={onAddMore}
              disabled={isSending}
              className="mr-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSending}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || localFiles.length === 0 || errors.length > 0}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Enviar {localFiles.length > 1 ? `(${localFiles.length})` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
